import { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Alert,
  Snackbar,
  InputAdornment,
  CircularProgress,
  Chip,
  styled,
} from '@mui/material';
import {
  Email,
  Phone,
  Person,
  Message,
  Send,
  CheckCircle,
  LocalHospital,
  RocketLaunch,
  SupportAgent,
  Schedule,
  VerifiedUser,
  FavoriteBorder,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { alpha, keyframes } from '@mui/material/styles';

const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

/* ─── Paleta basada en el logo ─── */
const C = {
  white:     '#ffffff',
  red:       '#f3353e',
  redDark:   '#d42d35',
  redLight:  '#ff6b72',
  navy:      '#164d70',
  navyDark:  '#0e3550',
  navyLight: '#1d6490',
  sky:       '#6cabd3',
  skyLight:  '#9ac5e2',
  skyPale:   '#e8f4fb',
  bg:        '#f7fafd',
  text1:     '#0e2a3f',
  text2:     '#5a7d95',
};

/* ─── Glassmorphism Card ─── */
const GlassCard = styled(Box)(() => ({
  background: alpha(C.white, 0.7),
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  borderRadius: 28,
  border: `1.5px solid ${alpha(C.sky, 0.2)}`,
  boxShadow: `0 8px 32px ${alpha(C.navy, 0.08)}, 0 1.5px 6px ${alpha(C.navy, 0.04)}`,
}));

/* ─── Keyframes ─── */
const pulse = keyframes`
  0%,100%{transform:scale(1);opacity:1}
  50%{transform:scale(1.04);opacity:.85}
`;

/* ─── Estilos reutilizables para inputs ─── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    background: alpha(C.white, 0.6),
    backdropFilter: 'blur(8px)',
    transition: 'all .3s ease',
    '&:hover': {
      background: alpha(C.white, 0.85),
    },
    '&:hover fieldset': {
      borderColor: alpha(C.sky, 0.6),
    },
    '&.Mui-focused': {
      background: C.white,
    },
    '&.Mui-focused fieldset': {
      borderColor: C.navy,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: C.navy,
    fontWeight: 600,
  },
};

const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  /* ── Validaciones ── */
  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const validatePhone = (p) => p.length >= 10 && /^[\d\s\-+()]+$/.test(p);

  const validate = () => {
    const err = {};
    if (!formData.name.trim()) err.name = 'El nombre es requerido';
    if (!formData.email.trim()) err.email = 'El email es requerido';
    else if (!validateEmail(formData.email)) err.email = 'Email inválido';
    if (formData.phone && !validatePhone(formData.phone)) err.phone = 'Teléfono inválido';
    if (!formData.message.trim()) err.message = 'El mensaje es requerido';
    else if (formData.message.trim().length < 10) err.message = 'El mensaje debe tener al menos 10 caracteres';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { setSnackbar({ open: true, message: 'Por favor corrige los errores en el formulario', severity: 'error' }); return; }
    setLoading(true);
    try {
      const payload = { nombre: formData.name.trim(), email: formData.email.trim(), mensaje: formData.message.trim(), pagina: window.location.pathname || 'Landing', userAgent: navigator.userAgent || '' };
      const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/+$/, '') || 'http://127.0.0.1:8000';
      const resp = await fetch(`${API_BASE}/api/contacto`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err?.error || 'No se pudo enviar el mensaje'); }
      const data = await resp.json();
      if (data.success) { setSnackbar({ open: true, message: '¡Mensaje enviado exitosamente! Te contactaremos pronto.', severity: 'success' }); setFormData({ name: '', email: '', phone: '', message: '' }); }
      else throw new Error('El servidor no confirmó el envío. Intenta de nuevo.');
    } catch (error) { console.error('Error al enviar:', error); setSnackbar({ open: true, message: error.message || 'Hubo un error al enviar el mensaje.', severity: 'error' }); }
    finally { setLoading(false); }
  };

  /* ── Animaciones ── */
  const containerV = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12 } } };
  const itemV = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, type: 'spring' } } };

  const contactInfo = [
    { icon: <Email sx={{ fontSize: 30 }} />, label: 'Email', value: 'info@app-medifyhc.com', color: C.navy },
    { icon: <Phone sx={{ fontSize: 30 }} />, label: 'Teléfono', value: '+1 (346) 252-9948', color: C.sky },
  ];

  const benefits = [
    { icon: <SupportAgent />, text: 'Soporte 24/7 especializado' },
    { icon: <RocketLaunch />, text: 'Implementación al instante' },
    { icon: <Schedule />, text: 'Capacitación gratuita incluida' },
    { icon: <VerifiedUser />, text: 'Sin costos ocultos' },
  ];

  return (
    <Box
      id="contacto"
      sx={{
        py: { xs: 10, md: 14 },
        background: `linear-gradient(160deg, ${alpha(C.sky, 0.06)} 0%, ${alpha(C.navy, 0.04)} 50%, ${alpha(C.red, 0.03)} 100%)`,
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Decoración de fondo */}
      <MotionBox
        animate={{ scale: [1, 1.2, 1], rotate: [0, 80, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
        sx={{
          position: 'absolute', top: -180, right: -180,
          width: 500, height: 500, borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(C.sky, 0.1)} 0%, transparent 70%)`,
          filter: 'blur(80px)',
        }}
      />
      <MotionBox
        animate={{ scale: [1, 1.3, 1], rotate: [0, -70, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
        sx={{
          position: 'absolute', bottom: -200, left: -200,
          width: 550, height: 550, borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(C.red, 0.07)} 0%, transparent 70%)`,
          filter: 'blur(90px)',
        }}
      />

      {/* Grid pattern */}
      <Box
        sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(${alpha(C.navy, 0.025)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(C.navy, 0.025)} 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <MotionBox variants={containerV} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }}>

          {/* Header */}
          <MotionBox variants={itemV} sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="CONTÁCTANOS"
              sx={{
                bgcolor: alpha(C.navy, 0.08), color: C.navy,
                fontWeight: 800, fontSize: '0.85rem',
                px: 2.5, py: 2.5, mb: 3, borderRadius: '12px',
              }}
            />
            <Typography variant="h3" fontWeight="900" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' }, color: C.text1 }}>
              ¿Listo para Transformar tu Consultorio?
            </Typography>
            <Typography variant="h6" sx={{ color: C.text2, maxWidth: 700, mx: 'auto', fontSize: { xs: '1.05rem', md: '1.2rem' }, lineHeight: 1.7 }}>
              Completa el formulario y un experto se pondrá en contacto contigo para mostrarte cómo{' '}
              <Box component="span" sx={{ fontWeight: 800, color: C.navy }}>Medify HealthCare</Box>{' '}
              puede revolucionar tu práctica médica
            </Typography>
          </MotionBox>

          <Grid container spacing={5} alignItems="stretch">
            {/* ── Columna izquierda: Info + Beneficios ── */}
            <Grid item xs={12} md={5}>
              <MotionBox variants={itemV} sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>

                {/* Card de contacto */}
                <GlassCard sx={{ p: { xs: 4, md: 5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Box sx={{
                      width: 56, height: 56, borderRadius: '16px',
                      background: `linear-gradient(135deg, ${C.navy} 0%, ${C.sky} 100%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: `0 8px 24px ${alpha(C.navy, 0.25)}`,
                    }}>
                      <LocalHospital sx={{ color: C.white, fontSize: 30 }} />
                    </Box>
                    <Typography variant="h5" fontWeight="900" sx={{ color: C.navy }}>
                      Información de Contacto
                    </Typography>
                  </Box>

                  <Typography sx={{ mb: 4, color: C.text2, lineHeight: 1.8, fontSize: '0.95rem' }}>
                    Estamos aquí para ayudarte. Contáctanos y recibe respuesta en menos de 24 horas.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {contactInfo.map((item, i) => (
                      <MotionBox
                        key={i}
                        initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 2.5,
                          p: 2.5, borderRadius: '16px',
                          background: alpha(item.color, 0.05),
                          transition: 'all .3s ease', cursor: 'pointer',
                          '&:hover': { background: alpha(item.color, 0.1), transform: 'translateX(6px)' },
                        }}
                      >
                        <Box sx={{
                          width: 50, height: 50, borderRadius: '14px', flexShrink: 0,
                          bgcolor: alpha(item.color, 0.12),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: `0 4px 12px ${alpha(item.color, 0.12)}`,
                        }}>
                          <Box sx={{ color: item.color }}>{item.icon}</Box>
                        </Box>
                        <Box>
                          <Typography sx={{ color: C.text2, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.3 }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body1" fontWeight="700" sx={{ color: item.color }}>
                            {item.value}
                          </Typography>
                        </Box>
                      </MotionBox>
                    ))}
                  </Box>
                </GlassCard>

                {/* Card de beneficios */}
                <GlassCard sx={{ p: { xs: 4, md: 5 }, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Box sx={{
                      width: 48, height: 48, borderRadius: '14px',
                      bgcolor: alpha('#22c55e', 0.1),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CheckCircle sx={{ color: '#22c55e', fontSize: 26 }} />
                    </Box>
                    <Typography variant="h5" fontWeight="800" sx={{ color: C.text1 }}>
                      ¿Por qué elegirnos?
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {benefits.map((b, i) => (
                      <MotionBox
                        key={i}
                        initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.35 + i * 0.08 }} viewport={{ once: true }}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 2,
                          p: 2, borderRadius: '14px',
                          transition: 'all .3s ease',
                          '&:hover': { bgcolor: alpha('#22c55e', 0.05), transform: 'translateX(5px)' },
                        }}
                      >
                        <Box sx={{
                          width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                          bgcolor: alpha('#22c55e', 0.08),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Box sx={{ color: '#22c55e', fontSize: 20 }}>{b.icon}</Box>
                        </Box>
                        <Typography variant="body1" fontWeight={600} sx={{ color: C.text1 }}>
                          {b.text}
                        </Typography>
                      </MotionBox>
                    ))}
                  </Box>
                </GlassCard>
              </MotionBox>
            </Grid>

            {/* ── Columna derecha: Formulario ── */}
            <Grid item xs={12} md={7}>
              <MotionPaper
                variants={itemV}
                sx={{
                  p: { xs: 4, md: 6 }, height: '100%',
                  borderRadius: '28px',
                  bgcolor: alpha(C.white, 0.85),
                  backdropFilter: 'blur(20px)',
                  border: `2px solid ${alpha(C.sky, 0.15)}`,
                  boxShadow: `0 20px 60px ${alpha(C.navy, 0.1)}, 0 0 0 1px ${alpha(C.sky, 0.05)}`,
                }}
              >
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Nombre */}
                    <Grid item xs={12}>
                      <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                        <TextField
                          fullWidth label="Nombre completo" name="name"
                          value={formData.name} onChange={handleChange}
                          error={!!errors.name} helperText={errors.name} disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person sx={{ color: C.navy, fontSize: 22 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />
                      </MotionBox>
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12} sm={6}>
                      <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}>
                        <TextField
                          fullWidth label="Email" name="email" type="email"
                          value={formData.email} onChange={handleChange}
                          error={!!errors.email} helperText={errors.email} disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email sx={{ color: C.navy, fontSize: 22 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />
                      </MotionBox>
                    </Grid>

                    {/* Teléfono */}
                    <Grid item xs={12} sm={6}>
                      <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }}>
                        <TextField
                          fullWidth label="Teléfono (opcional)" name="phone"
                          value={formData.phone} onChange={handleChange}
                          error={!!errors.phone} helperText={errors.phone} disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone sx={{ color: C.navy, fontSize: 22 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />
                      </MotionBox>
                    </Grid>

                    {/* Mensaje */}
                    <Grid item xs={12}>
                      <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.34 }}>
                        <TextField
                          fullWidth label="Mensaje" name="message" multiline rows={5}
                          value={formData.message} onChange={handleChange}
                          error={!!errors.message}
                          helperText={errors.message || `${formData.message.length}/500 caracteres`}
                          disabled={loading} inputProps={{ maxLength: 500 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                                <Message sx={{ color: C.navy, fontSize: 22 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={inputSx}
                        />
                      </MotionBox>
                    </Grid>

                    {/* Botón */}
                    <Grid item xs={12}>
                      <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42 }}>
                        <motion.div whileHover={{ scale: loading ? 1 : 1.02, y: -2 }} whileTap={{ scale: loading ? 1 : 0.97 }}>
                          <Button
                            type="submit" fullWidth variant="contained" size="large" disabled={loading}
                            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                            sx={{
                              py: 2, px: 4, fontSize: '1.15rem', fontWeight: 800,
                              borderRadius: '16px', textTransform: 'none', letterSpacing: 0.3,
                              bgcolor: C.red, color: C.white,
                              boxShadow: `0 8px 28px ${alpha(C.red, 0.35)}`,
                              transition: 'all .3s ease',
                              '&:hover': { bgcolor: C.redDark, boxShadow: `0 12px 36px ${alpha(C.red, 0.5)}` },
                              '&:disabled': { background: alpha(C.navy, 0.2), color: alpha(C.white, 0.6) },
                            }}
                          >
                            {loading ? 'Enviando...' : 'Enviar Mensaje'}
                          </Button>
                        </motion.div>
                      </MotionBox>
                    </Grid>

                    {/* Nota de privacidad */}
                    <Grid item xs={12}>
                      <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                          mt: 1, p: 2, borderRadius: '14px',
                          background: alpha(C.sky, 0.06),
                          border: `1px solid ${alpha(C.sky, 0.1)}`,
                        }}>
                          <CheckCircle sx={{ color: '#22c55e', fontSize: 18 }} />
                          <Typography variant="body2" sx={{ color: C.text2, fontSize: '0.85rem' }}>
                            Tus datos están seguros y protegidos. No compartimos tu información con terceros.
                          </Typography>
                        </Box>
                      </MotionBox>
                    </Grid>
                  </Grid>
                </form>
              </MotionPaper>
            </Grid>
          </Grid>
        </MotionBox>
      </Container>

      {/* Snackbar */}
      <AnimatePresence>
        {snackbar.open && (
          <Snackbar
            open={snackbar.open} autoHideDuration={6000}
            onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ bottom: { xs: 80, md: 24 } }}
          >
            <Alert
              onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
              severity={snackbar.severity} variant="filled"
              sx={{ width: '100%', borderRadius: '14px', fontWeight: 700, fontSize: '0.95rem' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default ContactForm;